<template>
    <div class="h-100">
        <b-container class="p-0" fluid>
            <b-row align-h="center">
                <b-col cols="12" md="10">
                    <b-link :to="{ name: 'settings-users' }"
                        ><font-awesome-icon icon="arrow-left" />
                        {{ $t("Back") }}
                    </b-link>
                    <h1 class="mt-1">
                        {{ $t("mainmenu.settings") }} / {{ $t("settings.users.title") }} /
                        {{ $t("settings.users.create.title") }}
                    </h1>

                    <hr />

                    <validation-observer v-if="userData" ref="validationRules" name="user" tag="form">
                        <AppUserInfo :user-data="userData" />
                        <AppChannels :channels="channels" :user-data="userData" />
                        <AppTeams :teams="teams" :user-data="userData" />

                        <h2 class="mb-1">
                            {{ $t("settings.users.create.advanced.title") }}
                        </h2>

                        <b-form-group
                            id="fieldset-send-email"
                            :description="$t('settings.users.create.advanced.send-email.description')"
                            :label="$t('settings.users.create.advanced.send-email.label')"
                            content-cols-lg="7"
                            content-cols-sm
                            label-cols-lg="3"
                            label-cols-sm="4"
                            label-for="sendEmail"
                        >
                            <b-form-checkbox
                                v-model="shouldSendInviteMail"
                                :unchecked-value="0"
                                :value="1"
                                inline
                                name="sendEmail"
                                switch
                            />
                        </b-form-group>

                        <hr />

                        <b-form-group content-cols-lg="7" content-cols-sm label-cols-lg="3" label-cols-sm="4">
                            <b-button
                                :disabled="isSaving"
                                class="d-flex justify-content-center mb-2"
                                variant="primary"
                                @click="create"
                            >
                                <b-spinner v-if="isSaving" class="mr-50" small></b-spinner>
                                {{ $t("settings.users.create.submit") }}
                            </b-button>
                        </b-form-group>
                    </validation-observer>
                </b-col>
            </b-row>
        </b-container>
    </div>
</template>
<script>
import { BButton, BFormInput, BContainer, BRow, BCol, BLink, BFormCheckbox, BFormGroup, BSpinner } from "bootstrap-vue";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import AppUserInfo from "./components/userInfo";
import AppChannels from "./components/channels";
import AppTeams from "./components/teams";
import { ref } from "@vue/composition-api/dist/vue-composition-api";
import axios from "@/libs/axios";
import useUsers from "./useUsers";
import { useToast } from "vue-toastification/composition";
import i18n from "@/libs/i18n";
import useUsersForm from "@/views/modules/settings/users/useUsersForm";

export default {
    components: {
        BButton,
        BFormInput,
        BRow,
        BCol,
        BLink,
        BContainer,
        AppUserInfo,
        AppChannels,
        AppTeams,
        ValidationObserver,
        BFormCheckbox,
        BFormGroup,
        BSpinner,
    },
    setup() {
        const toast = useToast();
        const userData = ref({
            role: "user",
            teams: [],
            channels: [],
        });
        const { teams, channels, sendInviteEmail, fetchChannels, fetchTeams, fetchLimits } = useUsers();
        const { getUserSettingsAsArray } = useUsersForm();
        const shouldSendInviteMail = 1;
        const isSaving = ref(false);

        fetchChannels();
        fetchTeams();

        return {
            userData,
            channels,
            teams,
            sendInviteEmail,
            shouldSendInviteMail,
            toast,
            isSaving,
            getUserSettingsAsArray,
            fetchLimits,
        };
    },
    methods: {
        create() {
            return new Promise((resolve, reject) => {
                this.$refs.validationRules.validate().then((success) => {
                    if (success) {
                        const channels = this.userData.channels.map((channelId) => {
                            return this.channels.find((channel) => channel.id === channelId);
                        });

                        const teams = this.userData.teams.map((teamId) => {
                            return this.teams.find((team) => team.id === teamId);
                        });

                        this.userData.settings = this.getUserSettingsAsArray();

                        this.isSaving = true;

                        axios
                            .post("users", {
                                ...this.userData,
                                teams,
                                channels,
                            })
                            .then(async (result) => {
                                if (this.shouldSendInviteMail) {
                                    await this.sendInviteEmail(this.userData.email).catch(() => {
                                        this.toast.warning(i18n.t("settings.users.created.error.sending-email"));
                                    });
                                }
                                this.$router.push({ name: "settings-users" }, () => {
                                    this.toast.success(i18n.t("settings.users.created.success.description"));
                                });
                                resolve(result.data);
                            })
                            .catch((error) => {
                                if (error?.response?.status === 409) {
                                    this.toast.error(
                                        i18n.t("settings.users.created.error.email-already-exists", {
                                            email: this.userData.email,
                                        })
                                    );
                                    return reject();
                                }

                                this.toast.error(
                                    i18n.t("toast.create.error", {
                                        entity: this.userData.name,
                                    })
                                );
                                reject();
                            })
                            .finally(() => {
                                this.isSaving = false;
                                this.fetchLimits(true);
                            });
                        resolve();
                    } else {
                        this.toast.error(
                            i18n.t("toast.create.error", {
                                entity: this.userData.name,
                            })
                        );
                        reject();
                    }
                });
            });
        },
    },
};
</script>
